/**
 * The local mirror: every read the app has ever successfully made, kept so the app works
 * with no connection (§8.2).
 *
 * This is storage only — deliberately no knowledge of HTTP, and no import of `api.ts`, so the
 * dependency runs one way and `request()` can use it without a cycle. `request()` decides
 * what counts as "could not reach the server"; this only remembers and recalls.
 *
 * **Why this exists at all.** The offline queue made *writes* survive being offline while
 * reads still went straight to the network — so the moment the server was unreachable the
 * screen emptied itself, and the queue faithfully preserved work the app then refused to
 * show. Patching that card by card produced exactly the "lots of things kinda broken" the
 * owner reported: areas that would not unfold, an inbox that could do nothing, undo behaving
 * differently offline than on. Caching centrally, at the one place every read already passes
 * through, is what makes offline stop being a special case (DECISIONS.md D166).
 *
 * `localStorage` rather than IndexedDB: whole-response snapshots, read and written as one
 * blob, synchronously, and pure convenience — losing them costs a reload, never data. The
 * write queue is the opposite (structured records that must survive at all costs) which is
 * why it lives in IndexedDB instead.
 */

const PREFIX = "efss.cache.";

/** Bumped when the stored shape changes incompatibly, so a stale entry is ignored rather
 * than deserialised into something the current code would misread. */
const VERSION = 1;

type Entry<T> = { version: number; at: number; body: T };

/** What the app last saw for `key`, or `undefined` if it has never seen it. */
export function cacheRead<T>(key: string): T | undefined {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as Entry<T>;
    return entry.version === VERSION ? entry.body : undefined;
  } catch {
    // Storage disabled, quota, or a half-written entry. A cache that throws is worse than no
    // cache at all — the caller only ever wanted the network result.
    return undefined;
  }
}

export function cacheWrite<T>(key: string, body: T): void {
  try {
    const entry: Entry<T> = { version: VERSION, at: Date.now(), body };
    window.localStorage.setItem(PREFIX + key, JSON.stringify(entry));
  } catch {
    /* full or unavailable — the app still works, it just won't remember this one */
  }
}

/**
 * Rewrite a cached entry in place, if there is one.
 *
 * This is what lets a queued write show up immediately: the change is applied to the local
 * mirror at the same moment it is queued for the server, so the screen reflects what you did
 * rather than what the server last said. Does nothing when there is no cached entry — there
 * is no view to correct, and inventing one would be guessing at data the app has never seen.
 */
export function cachePatch<T>(key: string, update: (body: T) => T): void {
  const current = cacheRead<T>(key);
  if (current === undefined) return;
  cacheWrite(key, update(current));
}

/** Every cached key, for the patchers that have to touch a family of them at once — the open
 * task list is cached once per energy ceiling and snoozed toggle, and a completed task has to
 * disappear from all of them, not just the one currently on screen. */
export function cacheKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key.slice(PREFIX.length));
    }
  } catch {
    /* storage unavailable — nothing is cached, so there is nothing to list */
  }
  return keys;
}

/** Fires when the app starts or stops serving from the mirror, so it can say so once rather
 * than every card saying it separately. */
export const SERVING_STALE_EVENT = "efss:serving-stale";

let stale = false;

/** Whether the most recent read fell back to the mirror. Cleared by the next real success. */
export function servingStale(): boolean {
  return stale;
}

export function setServingStale(next: boolean): void {
  if (stale === next) return;
  stale = next;
  window.dispatchEvent(new Event(SERVING_STALE_EVENT));
}

/** Drop the mirror — on a rejected token, so one reader's data is never shown behind
 * another's credential. */
export function clearReadCache(): void {
  try {
    for (const key of cacheKeys()) window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
  setServingStale(false);
}
